import React from "react";
import { Button, Card, CardBody } from "@nextui-org/react";
import { MapPinIcon } from "@heroicons/react/24/outline";
import ControlledInput from "./ControlledInput";
import LocationPicker from "./LocationPicker";
import useLocationPicker from "../../hooks/useLocationPicker";
import { useWatch } from "react-hook-form";
import ReactMapGL, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import toast from "react-hot-toast";

// ...existing code...

function isValidLatLng(lat, lng) {
  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);
  return !isNaN(latNum) && !isNaN(lngNum) && latNum >= -90 && latNum <= 90 && lngNum >= -180 && lngNum <= 180;
}

function MiniLocationMap({ latitude, longitude, address, className = "" }) {
  if (!latitude || !longitude) {
    return null;
  }

  if (!isValidLatLng(latitude, longitude)) {
    return (
      <div className={`mt-4 ${className}`}>
        <div className="bg-danger/10 border border-danger rounded-lg p-3 text-danger text-sm">Koordinat tidak valid. Latitude harus antara -90 dan 90, longitude antara -180 dan 180.</div>
      </div>
    );
  }

  const viewport = {
    latitude: parseFloat(latitude),
    longitude: parseFloat(longitude),
    zoom: 15,
  };

  return (
    <Card className={`mt-4 ${className}`} radius="sm">
      <CardBody className="p-3">
        <div className="space-y-3">
          <div className="text-sm">
            <p className="font-semibold text-ink-700">Lokasi Terpilih:</p>
            {address && <p className="text-ink-500 text-xs mt-1">{address}</p>}
            <p className="text-ink-500 text-xs">
              Koordinat: {parseFloat(latitude).toFixed(6)}, {parseFloat(longitude).toFixed(6)}
            </p>
          </div>

          <div className="w-full h-48 rounded-lg overflow-hidden">
            <ReactMapGL
              {...viewport}
              width="100%"
              height="100%"
              mapboxAccessToken="pk.eyJ1Ijoic2liMy1rbGhrIiwiYSI6ImNtMms1ZWVrZjAxb3MybHEyajE5cG5zb28ifQ.aCs5cuGJhZqytwo7zlVl5w"
              mapStyle="mapbox://styles/sib3-klhk/cm3i4eidd00dn01s9809ze69i"
              dragPan={true}
              scrollZoom={true}
              doubleClickZoom={true}
              touchZoom={true}
            >
              <Marker latitude={parseFloat(latitude)} longitude={parseFloat(longitude)}>
                <div
                  style={{
                    color: "#C03A2B",
                    fontSize: "24px",
                    transform: "translate(-50%, -100%)",
                  }}
                >
                  📍
                </div>
              </Marker>
            </ReactMapGL>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ...existing code...

export default function LocationInputWithPicker({ control, setValue, latitudeName = "latitude", longitudeName = "longitude", onLocationDataReceived, province, regency, district, village, isRequired = false }) {
  const { isOpen, selectedLocationData, openLocationPicker, closeLocationPicker, handleLocationSelect } = useLocationPicker();

  // Watch nilai latitude dan longitude dari form
  const latitude = useWatch({ control, name: latitudeName });
  const longitude = useWatch({ control, name: longitudeName });

  const handleLocationSelected = (locationData) => {
    if (validateCoordinates(locationData.latitude, locationData.longitude)) {
      setValue(latitudeName, locationData.latitude);
      setValue(longitudeName, locationData.longitude);

      if (onLocationDataReceived) {
        onLocationDataReceived(locationData);
      }
      handleLocationSelect(locationData);
    }
  };

  // Fungsi validasi koordinat
  function validateCoordinates(lat, lng) {
    if (lat !== undefined && (lat < -90 || lat > 90)) {
      toast.error("Nilai latitude harus antara -90 dan 90");
      return false;
    }
    if (lng !== undefined && (lng < -180 || lng > 180)) {
      toast.error("Nilai longitude harus antara -180 dan 180");
      return false;
    }
    return true;
  }

  // Handler input manual latitude
  const handleLatitudeChange = (value) => {
    const lat = parseFloat(value);
    if (validateCoordinates(lat, undefined)) {
      setValue(latitudeName, value);
    }
  };

  // Handler input manual longitude
  const handleLongitudeChange = (value) => {
    const lng = parseFloat(value);
    if (validateCoordinates(undefined, lng)) {
      setValue(longitudeName, value);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium">Koordinat Lokasi</label>
        <div className="flex justify-start">
          <Button color="secondary" variant="bordered" startContent={<MapPinIcon className="size-4" />} onPress={openLocationPicker} size="sm" className="w-auto">
            Pilih koordinat dari Peta
          </Button>
        </div>
      </div>

      {/* Tampilkan peta kecil jika sudah ada koordinat */}
      <MiniLocationMap latitude={latitude} longitude={longitude} address={selectedLocationData?.address} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <ControlledInput label="Latitude" name={latitudeName} type="number" step="0.0000001" control={control} placeholder="Masukkan Latitude" onChange={(e) => handleLatitudeChange(e.target.value)} isRequired={false} />
        <ControlledInput label="Longitude" name={longitudeName} type="number" step="0.0000001" control={control} placeholder="Masukkan Longitude" onChange={(e) => handleLongitudeChange(e.target.value)} isRequired={false} />
      </div>

      <LocationPicker isOpen={isOpen} onClose={closeLocationPicker} onLocationSelect={handleLocationSelected} />
    </>
  );
}
